import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state';
import { API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_ID_PATH_KEY } from '@/swagger/constants/surveys-keys';
import { API_GET_SURVEY_CAMPAIGN_BY_ID_CALL, KEY_GET_SURVEY_CAMPAIGN_BY_ID } from '../constats';
import { MOCK_RESPONSE_GET_SURVEY_CAMPAIGN_BY_ID } from '../mocks';

type RequestGetSurveyCampaignById = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_ID_PATH_KEY
>['request']['path'];

type ResponseGetSurveyCampaignById = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_ID_PATH_KEY
>['response']['body'];

type CallParamsGetSurveyCampaignById = Omit<RequestGetSurveyCampaignById, 'organizationId'> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetSurveyCampaignById = ResponseGetSurveyCampaignById;

type CallErrorGetSurveyCampaignById = GenericApiError;

type CallSelectorsGetSurveyCampaignById = {
  surveyTemplateId: ResponseGetSurveyCampaignById['surveyTemplateId'];
  totalInvited: number;
  isVisible: ResponseGetSurveyCampaignById['isVisible'];
};

export const getSurveyCampaignByIdCall = createGeneralCall<
  CallParamsGetSurveyCampaignById,
  CallResponseGetSurveyCampaignById,
  CallErrorGetSurveyCampaignById,
  CallSelectorsGetSurveyCampaignById
>({
  id: KEY_GET_SURVEY_CAMPAIGN_BY_ID,
  method: 'get',
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_SURVEY_CAMPAIGN_BY_ID_CALL(org, payload.id);
    },
  successMock: IS_MOCK_ENABLED({ avoidMockOnDemoMode: true })
    ? MOCK_RESPONSE_GET_SURVEY_CAMPAIGN_BY_ID
    : undefined,
  selectors: {
    surveyTemplateId: ({ selectors }) =>
      createSelector(selectors.response, res => res?.surveyTemplateId || '0'),
    totalInvited: ({ selectors }) =>
      createSelector(selectors.response, res => res?.invitedUserIds?.length || 0),
    isVisible: ({ selectors }) => createSelector(selectors.response, res => res?.isVisible),
  },
});
